import React from 'react';
import { ListItem, Text, UnorderedList } from '@chakra-ui/react';
import { LEGAL } from '../../config/legal';

export type LegalDocType = 'terms' | 'privacy' | 'cookies';

export interface LegalSection {
  id?: string;
  title: string;
  content: React.ReactNode;
}

export interface LegalNavActions {
  openTerms?: () => void;
  openPrivacy?: () => void;
}

export function getTermsSections(actions?: LegalNavActions): LegalSection[] {
  return [
    {
      title: '1. Agreement',
      content: (
        <Text>
          By creating an account or using {LEGAL.productName} (&quot;the Service&quot;), operated by{' '}
          {LEGAL.companyName}, you agree to these Terms of Service. If you use the Service on behalf of
          an organization, you represent that you have authority to bind that organization.
        </Text>
      ),
    },
    {
      title: '2. The Service',
      content: (
        <Text>
          The Service provides workspace-based project, client, and task management for software teams.
          Features vary by subscription plan. We may update, suspend, or discontinue features with
          reasonable notice where practicable.
        </Text>
      ),
    },
    {
      title: '3. Accounts & security',
      content: (
        <UnorderedList spacing={2} pl={4}>
          <ListItem>You must provide accurate registration information and keep credentials secure.</ListItem>
          <ListItem>You are responsible for activity under your account and workspace.</ListItem>
          <ListItem>You must notify us promptly of unauthorized access at {LEGAL.contactEmail}.</ListItem>
          <ListItem>We may suspend accounts that violate these terms or pose security risk.</ListItem>
        </UnorderedList>
      ),
    },
    {
      title: '4. Subscriptions & billing',
      content: (
        <UnorderedList spacing={2} pl={4}>
          <ListItem>Paid plans are billed per user per month via Stripe unless otherwise stated.</ListItem>
          <ListItem>Free trials convert to paid plans unless cancelled before the trial ends.</ListItem>
          <ListItem>Manage or cancel subscriptions through the billing portal in your account.</ListItem>
          <ListItem>Refunds are handled according to our billing policy and applicable law.</ListItem>
          <ListItem>Failure to pay may result in downgrade or suspension of paid features.</ListItem>
        </UnorderedList>
      ),
    },
    {
      title: '5. Acceptable use',
      content: (
        <Text>
          You may not use the Service to violate law, infringe rights, distribute malware, attempt
          unauthorized access, scrape or overload systems, or upload unlawful content. We may remove
          content or terminate access for violations.
        </Text>
      ),
    },
    {
      title: '6. Your data',
      content: (
        <Text>
          You retain ownership of workspace content you submit. You grant us a limited licence to host,
          process, and display that content solely to provide the Service. See our{' '}
          {actions?.openPrivacy ? (
            <Text as="button" type="button" color="blue.500" onClick={actions.openPrivacy}>
              Privacy Policy
            </Text>
          ) : (
            <Text as="span" color="blue.500">
              Privacy Policy
            </Text>
          )}{' '}
          for how we handle personal data, including export and deletion rights.
        </Text>
      ),
    },
    {
      title: '7. Intellectual property',
      content: (
        <Text>
          The Service, including software, branding, and documentation, is owned by {LEGAL.companyName}{' '}
          or its licensors. These terms do not grant you any rights to our trademarks or source code.
        </Text>
      ),
    },
    {
      title: '8. Disclaimers & liability',
      content: (
        <Text>
          The Service is provided &quot;as is&quot; to the fullest extent permitted by law. We do not
          warrant uninterrupted or error-free operation. Our total liability for claims arising from the
          Service is limited to fees paid by you in the twelve months before the claim, except where
          liability cannot be excluded by law.
        </Text>
      ),
    },
    {
      title: '9. Termination',
      content: (
        <Text>
          You may close your account or delete your workspace at any time via privacy settings. We may
          terminate or suspend access for breach of these terms. Upon termination, your right to use the
          Service ends; data deletion is handled per our Privacy Policy.
        </Text>
      ),
    },
    {
      title: '10. Changes',
      content: (
        <Text>
          We may update these terms. Material changes will be communicated via email or in-app notice.
          Continued use after the effective date constitutes acceptance. The &quot;Last updated&quot; date
          at the top reflects the current version.
        </Text>
      ),
    },
    {
      title: '11. Governing law',
      content: (
        <Text>
          These terms are governed by the laws of {LEGAL.governingLaw}. Disputes shall be subject to the
          exclusive jurisdiction of the courts of {LEGAL.governingLaw}, unless mandatory consumer
          protection law requires otherwise.
        </Text>
      ),
    },
  ];
}

export function getPrivacySections(actions?: LegalNavActions): LegalSection[] {
  return [
    {
      title: '1. Who we are',
      content: (
        <Text>
          {LEGAL.companyName} (&quot;we&quot;, &quot;us&quot;) operates {LEGAL.productName}. For privacy
          enquiries contact {LEGAL.contactEmail}. Our data protection contact: {LEGAL.dpoEmail}.
        </Text>
      ),
    },
    {
      title: '2. Data we collect',
      content: (
        <UnorderedList spacing={2} pl={4}>
          <ListItem>
            <strong>Account data:</strong> name, email, password (hashed), role, workspace membership.
          </ListItem>
          <ListItem>
            <strong>Workspace data:</strong> clients, projects, tasks, comments, files, and team activity
            you create in the Service.
          </ListItem>
          <ListItem>
            <strong>Billing data:</strong> subscription status and seat counts. Payment card details are
            processed by Stripe; we do not store full card numbers.
          </ListItem>
          <ListItem>
            <strong>Technical data:</strong> IP address, browser type, device information, and usage logs
            for security and service operation.
          </ListItem>
          <ListItem>
            <strong>Communications:</strong> support requests and notification preferences.
          </ListItem>
        </UnorderedList>
      ),
    },
    {
      title: '3. How we use data',
      content: (
        <UnorderedList spacing={2} pl={4}>
          <ListItem>Provide, maintain, and improve the Service.</ListItem>
          <ListItem>Authenticate users and enforce workspace access controls.</ListItem>
          <ListItem>Process subscriptions and send billing-related communications.</ListItem>
          <ListItem>Send in-app and email notifications you have opted into.</ListItem>
          <ListItem>Detect fraud, abuse, and security incidents.</ListItem>
          <ListItem>Comply with legal obligations.</ListItem>
        </UnorderedList>
      ),
    },
    {
      title: '4. Legal bases (UK/EU GDPR)',
      content: (
        <UnorderedList spacing={2} pl={4}>
          <ListItem>
            <strong>Contract:</strong> processing necessary to deliver the Service you signed up for.
          </ListItem>
          <ListItem>
            <strong>Legitimate interests:</strong> security, fraud prevention, and product improvement
            (balanced against your rights).
          </ListItem>
          <ListItem>
            <strong>Consent:</strong> non-essential cookies and optional marketing where applicable.
          </ListItem>
          <ListItem>
            <strong>Legal obligation:</strong> tax, accounting, and regulatory requirements.
          </ListItem>
        </UnorderedList>
      ),
    },
    {
      title: '5. Sharing & processors',
      content: (
        <Text>
          We do not sell personal data. We share data with subprocessors who help us operate the Service,
          including hosting, email delivery, payment processing (Stripe), and file storage (Cloudinary).
          Each processor is bound by data protection agreements. We may disclose data if required by law
          or to protect rights and safety.
        </Text>
      ),
    },
    {
      title: '6. International transfers',
      content: (
        <Text>
          Data may be processed outside your country. Where required, we use appropriate safeguards such as
          Standard Contractual Clauses or equivalent mechanisms approved under applicable law.
        </Text>
      ),
    },
    {
      title: '7. Retention',
      content: (
        <Text>
          We retain account and workspace data while your account is active. After deletion, we remove or
          anonymize data within 30 days except where longer retention is required for legal, security, or
          backup purposes. Backups may persist for up to 90 days before automatic purge.
        </Text>
      ),
    },
    {
      id: 'cookies',
      title: '8. Cookies & similar technologies',
      content: getCookiePolicyContent(),
    },
    {
      title: '9. Your rights',
      content: (
        <UnorderedList spacing={2} pl={4}>
          <ListItem>Access and receive a copy of your personal data.</ListItem>
          <ListItem>Rectify inaccurate data via profile settings.</ListItem>
          <ListItem>Export your data (JSON) from Profile → Privacy & data.</ListItem>
          <ListItem>Delete your account or workspace from the same section.</ListItem>
          <ListItem>Object to or restrict certain processing where applicable.</ListItem>
          <ListItem>Withdraw consent for optional cookies at any time.</ListItem>
          <ListItem>Lodge a complaint with your supervisory authority.</ListItem>
        </UnorderedList>
      ),
    },
    {
      title: '10. Children',
      content: (
        <Text>
          The Service is not directed at children under 16. We do not knowingly collect data from children.
          Contact us if you believe a child has provided personal data.
        </Text>
      ),
    },
    {
      title: '11. Changes',
      content: (
        <Text>
          We may update this policy. Material changes will be notified via email or in-app notice. The
          &quot;Last updated&quot; date reflects the current version. See also our{' '}
          {actions?.openTerms ? (
            <Text as="button" type="button" color="blue.500" onClick={actions.openTerms}>
              Terms of Service
            </Text>
          ) : (
            <Text as="span" color="blue.500">
              Terms of Service
            </Text>
          )}
          .
        </Text>
      ),
    },
  ];
}

export function getCookiePolicyContent() {
  return (
    <>
      <UnorderedList spacing={2} pl={4} mb={3}>
        <ListItem>
          <strong>Essential cookies:</strong> authentication session and security (always active).
        </ListItem>
        <ListItem>
          <strong>Preference cookies:</strong> UI settings such as sidebar state and cookie consent choice
          (stored in local storage).
        </ListItem>
        <ListItem>
          <strong>Analytics cookies (optional):</strong> only set if you accept analytics in our cookie
          banner. Used to understand product usage in aggregate.
        </ListItem>
      </UnorderedList>
      <Text>
        You can change cookie preferences below or via the cookie banner. Browser controls can also block
        cookies, though essential cookies are required for login.
      </Text>
    </>
  );
}

export function getCookieSections(): LegalSection[] {
  return [
    {
      title: 'Cookie policy',
      content: getCookiePolicyContent(),
    },
    {
      title: 'Managing preferences',
      content: (
        <Text>
          Use the buttons below to update your cookie preferences. Essential cookies cannot be disabled
          because they are required for authentication and security.
        </Text>
      ),
    },
  ];
}

export const LEGAL_TITLES: Record<LegalDocType, string> = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
  cookies: 'Cookie settings',
};
